import { api } from './apiClient';

/**
 * Typed client for the Goods Receipt (GRN) and Three-Way Match modules —
 * `/api/v1/goods-receipts` and `/api/v1/match`.
 *
 * Every apiClient verb auto-unwraps `{status,data}` to `data` (see
 * apiClient.ts). Both endpoints below still nest the payload one level
 * deeper under a named key, so `updateGoodsReceiptStatus` and
 * `updateTolerances` reach through it by hand via `unwrap()`.
 *
 * Numeric DECIMAL columns (quantities, unit_price, variance, variance_pct, ...)
 * come back from mysql2 as strings by default (no `decimalNumbers: true` on
 * the pool) — typed here as `number | string` so callers must parse before
 * doing arithmetic or formatting. `match_exceptions.expected_value` /
 * `actual_value` are VARCHAR columns on the server (matchService.js always
 * `String()`s whatever it records into them, including quantities, prices and
 * vendor ids), so they are plain `string | null` here, not numeric.
 */

// ---------------------------------------------------------------------------
// Reaches through the named-key nesting most endpoints put their payload
// under. apiClient already unwraps the outer `{status, data}` envelope for
// every verb (see apiClient.ts), so `body` here is already `{[key]: T}`.
// ---------------------------------------------------------------------------

const unwrap = <T,>(body: any, key: string): T => body?.[key];

/** Generic over the caller's filter shape — the request-body/query-param
 * interfaces below are closed (no index signature), so a plain
 * `Record<string, ...>` parameter type would reject every one of them. */
const qs = <T extends object>(params?: T): string => {
  if (!params) return '';
  const q = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
};

// ---------------------------------------------------------------------------
// GOODS RECEIPT
// ---------------------------------------------------------------------------

export const GRN_STATUSES = ['DRAFT', 'RECEIVED', 'QC_PENDING', 'ACCEPTED', 'REJECTED', 'CANCELLED'] as const;
export type GrnStatus = (typeof GRN_STATUSES)[number];

/** A goods-receipt header row, as goodsReceipt.js / GoodsReceipt.js return it. */
export interface GoodsReceipt {
  id: number;
  grn_number: string;
  po_id: number;
  vendor_id: number;
  warehouse_id: number | null;
  receipt_date: string;
  vendor_dc_number: string | null;
  vehicle_number: string | null;
  eway_bill_number: string | null;
  status: GrnStatus;
  remarks: string | null;
  received_by: number | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  vendor_name: string;
  vendor_code: string;
  po_number: string;
  warehouse_name: string | null;
}

export interface GoodsReceiptItem {
  id: number;
  grn_id: number;
  po_item_id: number;
  product_id: number | null;
  ordered_quantity: number | string;
  received_quantity: number | string;
  accepted_quantity: number | string;
  rejected_quantity: number | string;
  rejection_reason: string | null;
  unit_price: number | string;
  batch_number: string | null;
  expiry_date: string | null;
  created_at: string;
  product_name: string | null;
  product_code: string | null;
  unit_of_measure: string | null;
  po_ordered_quantity: number | string;
  po_unit_price: number | string;
}

export interface GoodsReceiptDetail extends GoodsReceipt {
  items: GoodsReceiptItem[];
}

/**
 * Declared as a type alias, not an interface, deliberately.
 *
 * The query-string helper takes `Record<string, string|number|boolean|null|undefined>`.
 * TypeScript gives object *type aliases* an implicit index signature but does
 * NOT give one to interfaces, so an interface with identical members fails to
 * satisfy that Record and the build breaks with a confusing
 * "Index signature for type 'string' is missing" error.
 */
export type GrnListFilters = {
  poId?: number | string;
  vendorId?: number | string;
  status?: GrnStatus | '';
  from?: string;
  to?: string;
};

export interface PendingPoHeader {
  id: number;
  po_number: string;
  vendor_id: number;
  status: string;
}

/** One open PO line — `pending_quantity` is server-computed (`GREATEST(ordered - received, 0)`). */
export interface PendingPoItem {
  po_item_id: number;
  product_id: number;
  product_name: string;
  product_code: string;
  unit_of_measure: string;
  ordered_quantity: number | string;
  received_quantity: number | string;
  unit_price: number | string;
  gst_rate: number | string | null;
  pending_quantity: number | string;
}

export interface CreateGoodsReceiptItemInput {
  poItemId: number;
  receivedQuantity: number;
  unitPrice?: number;
  batchNumber?: string;
  expiryDate?: string;
}

export interface LandedCostInput {
  costType: 'FREIGHT' | 'CUSTOMS' | 'INSURANCE' | 'CLEARING' | 'OVERHEAD' | 'OTHER';
  amount: number;
  vendorId?: number | null;
  allocationMethod?: 'VALUE' | 'QTY' | 'WEIGHT';
}

export interface CreateGoodsReceiptInput {
  poId: number;
  warehouseId: number;
  receiptDate: string;
  vendorDcNumber?: string | null;
  vehicleNumber?: string | null;
  ewayBillNumber?: string | null;
  remarks?: string | null;
  items: CreateGoodsReceiptItemInput[];
  // Landed costs (gap C): freight/customs/insurance apportioned across the
  // received lines and capitalised into inventory value at receipt.
  landedCosts?: LandedCostInput[];
}

export interface UpdateGrnStatusItemInput {
  id: number;
  acceptedQuantity?: number;
  rejectedQuantity?: number;
  rejectionReason?: string | null;
}

/** DRAFT is created implicitly by the server; CANCELLED only via `cancelGoodsReceipt`. */
export interface UpdateGrnStatusInput {
  status: Exclude<GrnStatus, 'DRAFT' | 'CANCELLED'>;
  items?: UpdateGrnStatusItemInput[];
}

// ---------------------------------------------------------------------------
// THREE-WAY MATCH
// ---------------------------------------------------------------------------

export const EXCEPTION_TYPES = [
  'NO_PO',
  'NO_GRN',
  'QTY_OVER_RECEIPT',
  'QTY_UNDER_RECEIPT',
  'PRICE_VARIANCE',
  'VALUE_VARIANCE',
  'DUPLICATE_INVOICE',
  'TAX_MISMATCH',
  'UOM_MISMATCH',
  'VENDOR_MISMATCH',
] as const;
export type ExceptionType = (typeof EXCEPTION_TYPES)[number];

export type ExceptionSeverity = 'BLOCKING' | 'WARNING';
export type ExceptionStatus = 'OPEN' | 'RESOLVED' | 'OVERRIDDEN';
export type InvoiceMatchStatus = 'UNMATCHED' | 'MATCHED' | 'WITHIN_TOLERANCE' | 'EXCEPTION' | 'OVERRIDDEN';

/** A persisted match_exceptions row, joined to its invoice/vendor by matchService.js. */
export interface MatchException {
  id: number;
  tenant_id: number;
  purchase_invoice_id: number;
  invoice_item_id: number | null;
  exception_type: ExceptionType;
  severity: ExceptionSeverity;
  expected_value: string | null;
  actual_value: string | null;
  variance: number | string | null;
  variance_pct: number | string | null;
  detail: string | null;
  status: ExceptionStatus;
  resolution_notes: string | null;
  resolved_by: number | null;
  resolved_at: string | null;
  created_at: string;
  invoice_number: string;
  vendor_invoice_number: string | null;
  match_status: InvoiceMatchStatus;
  vendor_name: string;
}

export interface MatchExceptionFilters {
  status?: ExceptionStatus | '';
  severity?: ExceptionSeverity | '';
  type?: ExceptionType | '';
  invoiceId?: number | string;
}

/** One line's live verdict, as returned only by the `evaluate()` response — never persisted. */
export interface MatchLineResult {
  invoiceItemId: number;
  verdict: 'MATCHED' | 'WITHIN_TOLERANCE' | 'EXCEPTION';
  reasons: Array<{ type: ExceptionType; severity: ExceptionSeverity }>;
}

export interface MatchEvaluateResult {
  status: InvoiceMatchStatus;
  lines: MatchLineResult[];
  exceptions: Array<{ id: number } & Record<string, unknown>>;
}

export interface MatchOverrideResult {
  exception: MatchException;
  invoiceId: number;
}

export interface MatchSummary {
  openBlocking: number;
  openWarning: number;
  byStatusSeverity: Array<{ status: ExceptionStatus; severity: ExceptionSeverity; cnt: number | string }>;
  byType: Array<{ exception_type: ExceptionType; cnt: number | string }>;
  byInvoiceMatchStatus: Array<{ match_status: InvoiceMatchStatus; cnt: number | string }>;
}

export interface MatchTolerances {
  id: number | null;
  tenant_id: number;
  name: string;
  qty_tolerance_pct: number | string;
  price_tolerance_pct: number | string;
  value_tolerance_abs: number | string;
  allow_over_receipt: number | boolean;
  require_grn: number | boolean;
  is_default: number | boolean;
}

export interface UpdateTolerancesInput {
  qtyTolerancePct?: number;
  priceTolerancePct?: number;
  valueToleranceAbs?: number;
  allowOverReceipt?: boolean;
  requireGrn?: boolean;
}

// ---------------------------------------------------------------------------
// Reference-data pickers (real /procurement and /inventory shapes — the
// generic `procurementService`/`inventoryService` types for these two lists
// don't match what the server actually returns, so these are typed directly
// against the controllers in erp-api/src/controllers/procurement.js and
// erp-api/src/models/Inventory.js rather than reused).
// ---------------------------------------------------------------------------

export interface PurchaseOrderOption {
  id: number;
  po_number: string;
  vendor_id: number;
  po_date: string;
  status: string;
  total_amount: number | string;
  vendor_name: string;
  vendor_code: string;
}

export interface PurchaseInvoiceOption {
  id: number;
  invoice_no: string;
  vendor_invoice_number: string | null;
  vendor_id: number;
  po_id: number | null;
  invoice_date: string;
  net_amount: number | string;
  status: string;
  vendor_name: string;
  po_number: string | null;
}

export interface WarehouseOption {
  id: number;
  warehouse_code: string;
  warehouse_name: string;
  is_active: number | boolean;
}

export const scmService = {
  // ── Goods Receipts ──────────────────────────────────────────────────────
  getGoodsReceipts: (filters?: GrnListFilters): Promise<{ goodsReceipts: GoodsReceipt[]; count: number }> =>
    api.get(`/goods-receipts${qs(filters)}`),

  getGoodsReceiptById: (id: number | string): Promise<{ goodsReceipt: GoodsReceiptDetail }> =>
    api.get(`/goods-receipts/${id}`),

  getPendingPoItems: (poId: number | string): Promise<{ po: PendingPoHeader; items: PendingPoItem[] }> =>
    api.get(`/goods-receipts/pending-po-items${qs({ poId })}`),

  createGoodsReceipt: (payload: CreateGoodsReceiptInput): Promise<{ goodsReceipt: GoodsReceiptDetail }> =>
    api.post('/goods-receipts', payload),

  updateGoodsReceiptStatus: (id: number | string, payload: UpdateGrnStatusInput): Promise<GoodsReceiptDetail> =>
    api.patch(`/goods-receipts/${id}/status`, payload).then((r) => unwrap<GoodsReceiptDetail>(r, 'goodsReceipt')),

  cancelGoodsReceipt: (id: number | string): Promise<{ goodsReceipt: GoodsReceiptDetail }> =>
    api.post(`/goods-receipts/${id}/cancel`),

  // ── Three-Way Match ──────────────────────────────────────────────────────
  evaluateMatch: (invoiceId: number | string): Promise<MatchEvaluateResult> =>
    api.post(`/match/evaluate/${invoiceId}`),

  getMatchExceptions: (filters?: MatchExceptionFilters): Promise<{ exceptions: MatchException[]; count: number }> =>
    api.get(`/match/exceptions${qs(filters)}`),

  getMatchExceptionById: (id: number | string): Promise<{ exception: MatchException }> =>
    api.get(`/match/exceptions/${id}`),

  overrideMatchException: (id: number | string, reason: string): Promise<MatchOverrideResult> =>
    api.post(`/match/exceptions/${id}/override`, { reason }),

  getMatchSummary: (): Promise<{ summary: MatchSummary }> => api.get('/match/summary'),

  getTolerances: (): Promise<{ tolerances: MatchTolerances }> => api.get('/match/tolerances'),

  updateTolerances: (payload: UpdateTolerancesInput): Promise<MatchTolerances> =>
    api.put('/match/tolerances', payload).then((r) => unwrap<MatchTolerances>(r, 'tolerances')),

  // ── Reference-data pickers ───────────────────────────────────────────────
  listPurchaseOrders: (params?: { vendorId?: number | string; status?: string }): Promise<{
    purchaseOrders: PurchaseOrderOption[];
    count: number;
  }> => api.get(`/procurement/purchase-orders${qs(params)}`),

  listPurchaseInvoices: (params?: { vendorId?: number | string; status?: string }): Promise<{
    invoices: PurchaseInvoiceOption[];
    summary: unknown;
    count: number;
  }> => api.get(`/procurement/purchase-invoices${qs(params)}`),

  listWarehouses: (): Promise<{ warehouses: WarehouseOption[]; count: number }> =>
    api.get('/inventory/warehouses'),
};

export default scmService;
